// Raw Paystack API response shapes

export interface PaystackApiResponse<T> {
  data: T;
  message: string;
  status: boolean;
}

export interface PaystackCustomer {
  createdAt: string;
  customer_code: string;
  domain: string;
  email: string;
  first_name: string | null;
  id: number;
  identifications: unknown[] | null;
  identified: boolean;
  international_format_phone: string | null;
  last_name: string | null;
  metadata: Record<string, unknown> | null;
  phone: string | null;
  risk_action: string;
  updatedAt: string;
}

export interface PaystackAuthorization {
  account_name: string | null;
  authorization_code: string;
  bank: string;
  bin: string;
  brand: string;
  card_type: string;
  channel: string;
  country_code: string;
  exp_month: string;
  exp_year: string;
  last4: string;
  reusable: boolean;
  signature: string;
}

export interface PaystackPlan {
  amount: number; // in kobo/cents
  createdAt: string;
  currency: string;
  description: string | null;
  domain: string;
  hosted_page: boolean;
  hosted_page_summary: string | null;
  hosted_page_url: string | null;
  id: number;
  integration: number;
  interval: "hourly" | "daily" | "weekly" | "monthly" | "annually";
  name: string;
  plan_code: string;
  send_invoices: boolean;
  send_sms: boolean;
  updatedAt: string;
}

export interface PaystackSubscription {
  amount: number; // in kobo/cents
  authorization: PaystackAuthorization;
  createdAt: string;
  cron_expression: string;
  customer: PaystackCustomer;
  domain: string;
  easy_cron_id: string | null;
  email_token: string;
  id: number;
  integration: number;
  invoices: unknown[];
  next_payment_date: string;
  open_invoice: string | null;
  plan: PaystackPlan;
  quantity: number;
  start: number; // Unix timestamp
  status: "active" | "complete" | "cancelled" | "inactive" | "non-renewing";
  subscription_code: string;
  updatedAt: string;
}

export interface PaystackTransaction {
  amount: number; // in kobo/cents
  authorization: PaystackAuthorization;
  channel: string;
  connect: unknown | null;
  created_at: string;
  createdAt: string;
  currency: string;
  customer: PaystackCustomer;
  domain: string;
  fees: number;
  fees_breakdown: unknown | null;
  fees_split: unknown | null;
  gateway_response: string;
  id: number;
  ip_address: string;
  log: {
    start_time: number;
    time_spent: number;
    attempts: number;
    errors: number;
    success: boolean;
    mobile: boolean;
    input: unknown[];
    history: Array<{
      type: string;
      message: string;
      time: number;
    }>;
  };
  message: string | null;
  metadata: Record<string, unknown> | null;
  order_id: string | null;
  paid_at: string;
  paidAt: string;
  plan: PaystackPlan | null;
  plan_object: unknown;
  pos_transaction_data: unknown | null;
  receipt_number: string | null;
  reference: string;
  requested_amount: number;
  source: {
    type: string;
    source: string;
    identifier: string | null;
  } | null;
  split: unknown;
  status: "success" | "failed" | "abandoned";
  subaccount: unknown;
  transaction_date: string;
}

// Portal-friendly transformed types (consumed by billing app UI)

export interface CustomerData {
  createdAt: string;
  customer_code: string;
  display_name: string;
  domain: string;
  email: string;
  first_name: string | null;
  id: number;
  identified: boolean;
  last_name: string | null;
  metadata: Record<string, unknown> | null;
  // Computed
  name: string;
  phone: string | null;
  risk_action: string;
  updatedAt: string;
}

export interface SubscriptionData {
  authorization?: {
    bank: string | null;
    brand: string;
    card_type: string | null;
    exp_month: string;
    exp_year: string;
    last4: string;
  } | null;
  cancel_at_period_end: boolean;
  current_period_end: string;
  current_period_start: string;
  email_token: string;
  id: string;
  next_payment_date: string;
  plan: {
    id: string;
    plan_code: string;
    name: string;
    amount: number; // major currency unit
    currency: string;
    interval: "monthly" | "yearly" | "weekly" | "daily" | "hourly" | "annually";
    description: string | null;
  };
  quantity: number;
  status:
    | "active"
    | "cancelled"
    | "past_due"
    | "incomplete"
    | "trialing"
    | "non-renewing"
    | "inactive"
    | "complete";
  subscription_code: string;
}

export interface PaymentMethodData {
  account_name: string | null;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    bank: string;
    card_type: string;
    bin: string;
  };
  id: string; // authorization_code
  is_default: boolean;
  reusable: boolean;
  type: "card";
}

export interface InvoiceData {
  amount: number; // major currency unit
  authorization?: { brand: string; last4: string } | null;
  channel: string | null;
  created: string;
  currency: string | null;
  description: string | null;
  fees: number;
  gateway_response: string;
  id: string;
  paid_at: string | null;
  plan_name: string | null;
  reference: string;
  status: "paid" | "failed" | "pending" | "success" | "abandoned" | "refunded";
  transaction_id: number | null;
}

export interface PlanData {
  amount: number; // major currency unit
  currency: string;
  description: string | null;
  id: string;
  interval: "monthly" | "yearly" | "weekly" | "daily" | "hourly" | "annually";
  name: string;
  plan_code: string;
  send_invoices: boolean;
  send_sms: boolean;
}

export class PortalApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "PortalApiError";
    this.status = status;
    this.code = code;
  }
}
